import { useApolloClient } from "@apollo/react-hooks";
import {
  useBraze,
  Header,
  MenuItemType,
} from "@customer-tools-web/component-header-footer";
import * as React from "react";

import { listingQuery } from "../../__generated__/listings";
import { addListingRoutes } from "../../routing/routeConfig";
import { matchRoutes } from "../../routing/routeConfigTools";
import { URL_PREFIX } from "../common/const";
import { GA, GA_ACTIONS } from "../common/ga-tracking/const";
import { generateStepLabel } from "../common/ga-tracking/trackTools";
import { useTriggerGAEvent } from "../common/ga-tracking/withTriggerGAEvent";
import {
  LISTING_QUERY,
  MARKETWATCH_NOTIFICATION_UNREAD_COUNT_QUERY,
} from "../common/queries";
import { steps } from "../common/stepsConfiguration";
import { useHeader } from "./contexts/HeaderContext";
import featureToggles from "../../config/feature-toggles.malaysia";

const CHECK_FOR_UNREAD_NOTIFICATIONS_EVENT = "checkForUnreadNotifications";

type Props = {
  shouldUseRouter?: boolean;
  recordHeaderHeight: (height: number) => void;
};

function HeaderComponent(props: Props) {
  const headerRef = useHeader();
  const client = useApolloClient();
  const triggerGAEvent = useTriggerGAEvent();
  const braze = useBraze();
  const [marketwatchStatistics, setMarketwatchStatistics] = React.useState({
    unread: 0,
  });

  const isMarketWatchEnabled = featureToggles.MARKET_WATCH;

  function getListingFromStore(listingId: string) {
    const listingQuery = client.readQuery<listingQuery>({
      query: LISTING_QUERY,
      variables: { id: listingId },
    });
    return listingQuery && listingQuery.listing;
  }

  function getMarketWatchNotificationUnreadCount() {
    client
      .query({
        query: MARKETWATCH_NOTIFICATION_UNREAD_COUNT_QUERY,
        variables: { getCount: true },
        fetchPolicy: "network-only",
      })
      .then((res) => {
        const { mwNotificationUnreadCount } = res.data;
        const unreadCount = mwNotificationUnreadCount?.count || 0;

        setMarketwatchStatistics({ unread: unreadCount });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  async function trackMenuTap(
    labelKey: MenuItemType["key"],
    fromLink?: string
  ) {
    const addListingRoute = fromLink && matchRoutes(addListingRoutes, fromLink);
    if (["listings", "reportings"].indexOf(labelKey) > -1 && addListingRoute) {
      const stepIndex = steps.findIndex((step) =>
        step.path.includes(addListingRoute.params.stepName)
      );
      const label =
        stepIndex !== -1
          ? generateStepLabel(stepIndex + 1, steps[stepIndex].gaLabel)
          : "";
      triggerGAEvent(
        GA.addListing["Tap on Listings tab on top to exit creation"]({
          event_label: label,
        }),
        {
          listing: addListingRoute.params.id
            ? getListingFromStore(addListingRoute.params.id)
            : null,
        }
      );
    } else {
      const action = GA_ACTIONS.navigation[labelKey];
      if (action) {
        triggerGAEvent(GA.navigation[action]());
      }
    }
  }

  React.useEffect(() => {
    if (headerRef?.current) {
      props.recordHeaderHeight(headerRef?.current?.clientHeight);
    }
  }, []);

  React.useEffect(() => {
    if (!isMarketWatchEnabled) return;

    getMarketWatchNotificationUnreadCount();

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleMessage = (event: any) => {
    const eventName = event?.data?.event;

    if (eventName === CHECK_FOR_UNREAD_NOTIFICATIONS_EVENT) {
      getMarketWatchNotificationUnreadCount();
    }
  };

  const { shouldUseRouter } = props;
  const isOldDomain = /\/\/web\./.test(window.location.origin);
  let umServer = "";
  let ctServer = "";
  if (isOldDomain) {
    umServer = window.location.origin.replace("cust-tools", "user-management");
    ctServer = window.location.origin.replace("user-management", "cust-tools");
  }
  return (
    <div ref={headerRef}>
      <Header
        withPermission
        data-test="header"
        trackMenuTap={trackMenuTap}
        umServer={umServer}
        ctServer={`${ctServer}${URL_PREFIX}`}
        shouldUseRouter={shouldUseRouter === undefined ? true : shouldUseRouter}
        marketwatchStatistic={marketwatchStatistics}
        braze={braze}
      />
    </div>
  );
}
export default HeaderComponent;
